// src/contexts/AuthContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signOut, 
  sendPasswordResetEmail,
  sendEmailVerification, 
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  serverTimestamp, 
  addDoc
} from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [creatorPodcasts, setCreatorPodcasts] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Logout function
  function logout() {
    return signOut(auth);
  }
  
  // Password reset function
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }
  
  // Email verification
  function verifyEmail() {
    if (!currentUser) return Promise.reject("No user logged in");
    return sendEmailVerification(currentUser);
  }
  
  // Update user profile in Firebase Auth
  function updateProfileInfo(displayName, photoURL) {
    if (!currentUser) return Promise.reject("No user logged in");
    return updateProfile(currentUser, {
      displayName: displayName || currentUser.displayName,
      photoURL: photoURL || currentUser.photoURL
    });
  }

  // Fetch user profile data from Firestore with real-time updates
  function setupProfileListener(uid) {
    const userDocRef = doc(db, "users", uid);
    
    // Set up real-time listener
    return onSnapshot(userDocRef, 
      (doc) => {
        if (doc.exists()) {
          setUserProfile(doc.data());
        } else {
          // Profile doesn't exist, create a default one
          createDefaultProfile(uid);
        }
        setError(null);
      },
      (err) => {
        console.error("Error in profile listener:", err);
        setError("Failed to load profile data");
      }
    );
  }
  
  // Create default profile if none exists
  async function createDefaultProfile(uid) {
    try {
      const user = auth.currentUser;
      
      if (!user) return;
      
      const defaultProfile = {
        email: user.email,
        username: user.displayName || user.email.split('@')[0], // Simple default username
        isCreator: false,
        isAdmin: false, // Add admin field to default profile
        dateJoined: serverTimestamp(),
        profilePicURL: user.photoURL || '',
        bio: '',
        favoriteCategories: [],
        followedPodcasts: [],
        isEmailVerified: user.emailVerified,
        listeningHistory: [],
        dateOfBirth: null,
        country: '',
        preferredLanguage: 'en',
        gender: null,
        city: '',
        interests: [],
        preferredDevices: [],
        lastUpdated: serverTimestamp(),
        dataConsent: {
          analyticsEnabled: false,
          personalizationEnabled: false,
          marketingEnabled: false,
          dataProcessingAccepted: true,
          lastUpdated: serverTimestamp()
        },
        notificationPreferences: {
          newEpisodes: true,
          creatorUpdates: true,
          recommendations: true,
          systemUpdates: true
        }
      };
      
      await setDoc(doc(db, "users", uid), defaultProfile);
      setUserProfile(defaultProfile);
    } catch (error) {
      console.error("Error creating default profile:", error);
      setError("Failed to create user profile");
    }
  }
  
  // Check subscription status
  async function checkSubscriptionStatus() {
    if (!currentUser) return null;
    
    try {
      const subscriptionDoc = await getDoc(doc(db, "subscriptions", currentUser.uid));
      if (subscriptionDoc.exists()) {
        return subscriptionDoc.data();
      }
      return null;
    } catch (error) {
      console.error("Error checking subscription:", error);
      return null;
    }
  }

  // Update user profile in Firestore
  async function updateUserProfile(data) {
    if (!currentUser) return { success: false, message: "No user logged in" };
    if (userProfile?.isSuspended) return { success: false, message: "Konto suspenderet — ingen ændringer tilladt." };

    try {
      // Strip fields that must never be client-writable
      const { isAdmin, isCreator, subscriptionStatus, ...safeData } = data;

      const updateData = {
        ...safeData,
        lastUpdated: serverTimestamp()
      };
      
      await updateDoc(doc(db, "users", currentUser.uid), updateData);
      
      // CRITICAL: Update local state immediately for instant UI updates
      setUserProfile(prev => ({
        ...prev,
        ...safeData,  // Use safeData — privileged fields already stripped
        lastUpdated: new Date() // Use local timestamp for immediate UI update
      }));
      
      return { success: true };
    } catch (error) {
      console.error("Error updating user profile:", error);
      setError("Failed to update profile");
      return { success: false, message: error.message };
    }
  }
  
  // Fetch user's podcasts if they are a creator
  async function fetchCreatorPodcasts() {
    if (!currentUser || !userProfile?.isCreator) {
      setCreatorPodcasts([]);
      return [];
    }
    
    try {
      // Get podcasts where the user is the creator
      const podcastsQuery = query(
        collection(db, "user_podcasts"),
        where("userId", "==", currentUser.uid)
      );
      
      const snapshot = await getDocs(podcastsQuery);
      
      if (snapshot.empty) {
        setCreatorPodcasts([]);
        return [];
      }
      
      // Fetch the actual podcast data
      const podcastPromises = snapshot.docs.map(async (userPodcastDoc) => {
        const { podcastId } = userPodcastDoc.data();
        const podcastDoc = await getDoc(doc(db, "podcasts", podcastId));
        
        if (podcastDoc.exists()) {
          return { id: podcastId, ...podcastDoc.data() };
        }
        return null;
      });
      
      const podcasts = (await Promise.all(podcastPromises)).filter(p => p !== null);
      setCreatorPodcasts(podcasts);
      return podcasts;
    } catch (error) {
      console.error("Error fetching creator podcasts:", error);
      setError("Failed to load your podcasts");
      return [];
    }
  }
  
  // Register as a creator
  async function registerAsCreator(creatorDetails) {
    if (!currentUser) return { success: false, message: "No user logged in" };
    if (userProfile?.isSuspended) return { success: false, message: "Konto suspenderet — ingen ændringer tilladt." };
    
    try {
      // Update user profile with creator status
      await updateDoc(doc(db, "users", currentUser.uid), {
        isCreator: true,
        creatorSince: serverTimestamp(),
        creatorDetails: {
          ...creatorDetails,
          verified: false, // Requires verification
          status: 'pending' // Pending review
        },
        lastUpdated: serverTimestamp()
      });
      
      // Create a verification request
      await addDoc(collection(db, "creator_verifications"), {
        userId: currentUser.uid,
        status: 'pending',
        creatorDetails,
        submittedAt: serverTimestamp()
      });
      
      return { 
        success: true, 
        message: "Creator registration submitted for review" 
      };
    } catch (error) {
      console.error("Error registering as creator:", error);
      setError("Failed to register as creator");
      return { success: false, message: error.message };
    }
  }
  
  // Verify podcast ownership
  async function verifyPodcastOwnership(rssUrl) {
    if (!currentUser) return { success: false, message: "No user logged in" };
    if (userProfile?.isSuspended) return { success: false, message: "Konto suspenderet — ingen ændringer tilladt." };
    
    try {
      // Generate verification token
      const verificationToken = `faircast-${currentUser.uid}-${Date.now()}`;
      
      // Store verification request
      await setDoc(doc(db, "podcast_verifications", `${currentUser.uid}_${rssUrl}`), {
        userId: currentUser.uid,
        rssUrl,
        verificationToken,
        status: 'pending',
        createdAt: serverTimestamp(),
        attempts: 0
      });
      
      return {
        success: true,
        verificationToken,
        instructions: "Add this token to your podcast description to verify ownership"
      };
    } catch (error) {
      console.error("Error initiating podcast verification:", error);
      setError("Failed to start verification process");
      return { success: false, message: error.message };
    }
  }

  // Listen for auth state changes
  useEffect(() => {
    let profileUnsubscribe = () => {};
    
    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Set up real-time listener for profile
        profileUnsubscribe = setupProfileListener(user.uid);
        
        // Check subscription once
        const sub = await checkSubscriptionStatus();
        setSubscription(sub);
        
        // Fetch creator podcasts if applicable
        fetchCreatorPodcasts();
      } else {
        setUserProfile(null);
        setSubscription(null);
        setCreatorPodcasts([]);
      }
      
      setLoading(false);
    });
  
    // Clean up listeners
    return () => {
      authUnsubscribe();
      profileUnsubscribe();
    };
  }, []);
  
  // Refetch creator podcasts when creator status changes
  useEffect(() => {
    if (userProfile?.isCreator) {
      fetchCreatorPodcasts();
    }
  }, [userProfile?.isCreator]);
  
  // Define the value object for context
  const value = {
    currentUser,
    userProfile,
    subscription,
    creatorPodcasts,
    loading,
    error,
    logout,
    resetPassword,
    verifyEmail,
    updateProfileInfo,
    updateUserProfile,
    fetchCreatorPodcasts,
    registerAsCreator,
    verifyPodcastOwnership,
    isCreator: userProfile?.isCreator || false,
    isAdmin: userProfile?.isAdmin || false,
    isSuspended: userProfile?.isSuspended || false,
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}